/**
 * Export Excel riche et formaté de l'historique de cotisations d'un membre.
 *
 * Génère un classeur avec deux feuilles :
 *  - « Mes contributions » : chaque versement (date, type, montant, méthode,
 *                            statut, n° transaction, téléphone, reçu, note) avec
 *                            bandeau titre, en-tête figé, filtres automatiques,
 *                            zébrures, couleur de statut et ligne de totaux.
 *  - « Synthèse »          : totaux (validé, en attente, rejeté, ce mois) et
 *                            répartition par type de contribution.
 *
 * Le formatage (couleurs, gras, bordures, formats nombre) repose sur ExcelJS,
 * la version communautaire de SheetJS (`xlsx`) ne sachant pas styliser.
 * Aligné sur `membersExcel.js` pour une cohérence visuelle des exports.
 */
import { isConfirmed, isPending, isRejected, toDate, inSameMonth } from './contributions';

// ---- Palette (identique aux autres exports) ----
const NAVY = 'FF0B1F3A';
const GREEN = 'FF0F5132';
const GREEN_LIGHT = 'FFE7F1EC';
const ZEBRA = 'FFF5F7FA';
const GREY_BORDER = 'FFD9DEE5';
const AMBER = 'FF9A6700';
const RED = 'FFB42318';
const SLATE = 'FF475467';

const MONEY_FMT = '#,##0" FCFA"';
const PCT_FMT = '0"%"';

const thin = { style: 'thin', color: { argb: GREY_BORDER } };
const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

const fmtDate = (c) => {
  const d = toDate(c);
  return d ? d.toLocaleDateString('fr-FR') : '';
};

const statusLabel = (c) => (isConfirmed(c) ? 'Confirmé' : isRejected(c) ? 'Rejeté' : 'En attente');

/**
 * Construit le classeur et déclenche son téléchargement.
 * @param {Array} contributions  Liste des contributions (déjà filtrées si besoin).
 * @param {object} opts
 * @param {Array}  opts.contributionTypes  Types configurés (pour les libellés).
 * @param {Array}  opts.paymentMethods     Méthodes de paiement (pour les libellés).
 * @param {string} opts.memberName         Nom du membre (sous-titre).
 * @param {number} opts.year               Année de référence (nom de fichier).
 * @returns {Promise<number>} Nombre de contributions exportées.
 */
export async function exportContributionsToExcel(contributions, { contributionTypes = [], paymentMethods = [], memberName = '', year = new Date().getFullYear() } = {}) {
  // exceljs est volumineux : chargé à la demande pour rester hors du bundle principal.
  const ExcelJS = (await import('exceljs')).default;

  const typeLabel = (c) => {
    if (c.eventId) return c.eventTitle || 'Événement';
    const t = contributionTypes.find((x) => x.id === c.type);
    return t?.label || c.type || 'Autre';
  };
  const methodLabel = (m) => paymentMethods.find((p) => p.value === m)?.label || m || '';

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Daara Wakana';
  wb.created = new Date();

  buildHistorySheet(wb, contributions, { typeLabel, methodLabel, memberName });
  buildSummarySheet(wb, contributions, { typeLabel });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeName = (memberName || 'membre').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  a.download = `contributions_${safeName}_${year}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return contributions.length;
}

// ---- Feuille « Mes contributions » ----

function buildHistorySheet(wb, contributions, { typeLabel, methodLabel, memberName }) {
  const ws = wb.addWorksheet('Mes contributions', {
    views: [{ state: 'frozen', ySplit: 4 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });

  const columns = [
    { header: '#', key: 'idx', width: 5 },
    { header: 'Date', key: 'date', width: 14 },
    { header: 'Type', key: 'type', width: 22 },
    { header: 'Montant', key: 'amount', width: 16, money: true },
    { header: 'Méthode', key: 'method', width: 16 },
    { header: 'Statut', key: 'status', width: 14 },
    { header: 'N° transaction', key: 'txn', width: 20 },
    { header: 'Téléphone', key: 'phone', width: 16 },
    { header: 'Reçu', key: 'receipt', width: 9 },
    { header: 'Note', key: 'note', width: 30 }
  ];
  const lastCol = columns.length;

  // Bandeau titre (lignes 1-3)
  ws.mergeCells(1, 1, 1, lastCol);
  const title = ws.getCell(1, 1);
  title.value = memberName ? `Cotisations & Dons — ${memberName}` : 'Cotisations & Dons';
  title.font = { name: 'Calibri', size: 18, bold: true, color: { argb: 'FFFFFFFF' } };
  title.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(1).height = 30;

  ws.mergeCells(2, 1, 2, lastCol);
  const sub = ws.getCell(2, 1);
  const confirmedCount = contributions.filter(isConfirmed).length;
  sub.value = `${contributions.length} contribution(s) · ${confirmedCount} confirmée(s) · Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  sub.font = { name: 'Calibri', size: 11, color: { argb: 'FFC7D2E0' } };
  sub.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(2).height = 20;

  for (let r = 1; r <= 2; r++) {
    for (let c = 1; c <= lastCol; c++) {
      ws.getCell(r, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    }
  }
  ws.getRow(3).height = 6;

  // En-tête de tableau (ligne 4)
  const headerRowIdx = 4;
  const headerRow = ws.getRow(headerRowIdx);
  columns.forEach((col, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = col.header;
    cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = allBorders;
  });
  headerRow.height = 24;

  // Lignes de données
  let row = headerRowIdx + 1;
  contributions.forEach((c, i) => {
    const values = [
      i + 1,
      fmtDate(c),
      typeLabel(c),
      c.amount || 0,
      methodLabel(c.paymentMethod),
      statusLabel(c),
      c.transactionId || '',
      c.phoneNumber || '',
      c.receiptUrl ? 'Oui' : '—',
      c.description || ''
    ];

    const r = ws.getRow(row);
    values.forEach((v, ci) => {
      const cell = r.getCell(ci + 1);
      cell.value = v;
      cell.border = allBorders;
    });

    r.getCell(4).numFmt = MONEY_FMT;
    r.getCell(1).alignment = { horizontal: 'center' };
    r.getCell(2).alignment = { horizontal: 'center' };
    r.getCell(6).alignment = { horizontal: 'center' };
    r.getCell(9).alignment = { horizontal: 'center' };

    // Couleur du statut
    const statusCell = r.getCell(6);
    statusCell.font = { bold: true, color: { argb: isConfirmed(c) ? GREEN : isRejected(c) ? RED : AMBER } };

    // Zébrures
    if (i % 2 === 1) {
      for (let ci = 1; ci <= lastCol; ci++) {
        const cell = r.getCell(ci);
        if (!cell.fill || cell.fill.type !== 'pattern') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
        }
      }
    }
    row++;
  });

  // Ligne de totaux (montant confirmé)
  const totalRow = ws.getRow(row);
  const labelCell = totalRow.getCell(3);
  labelCell.value = 'TOTAL CONFIRMÉ';
  labelCell.font = { bold: true, color: { argb: GREEN } };
  labelCell.alignment = { horizontal: 'right' };
  const totalConfirmed = contributions.filter(isConfirmed).reduce((s, c) => s + (c.amount || 0), 0);
  const totalCell = totalRow.getCell(4);
  totalCell.value = totalConfirmed;
  totalCell.numFmt = MONEY_FMT;
  totalCell.font = { bold: true, color: { argb: GREEN } };
  for (let c = 1; c <= lastCol; c++) {
    const cell = totalRow.getCell(c);
    cell.border = { top: { style: 'medium', color: { argb: GREEN } }, bottom: thin, left: thin, right: thin };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_LIGHT } };
  }
  totalRow.height = 22;

  ws.autoFilter = {
    from: { row: headerRowIdx, column: 1 },
    to: { row: headerRowIdx, column: lastCol }
  };
}

// ---- Feuille « Synthèse » ----

function buildSummarySheet(wb, contributions, { typeLabel }) {
  const ws = wb.addWorksheet('Synthèse', { pageSetup: { orientation: 'portrait' } });
  ws.columns = [{ width: 34 }, { width: 18 }, { width: 18 }, { width: 12 }];

  ws.mergeCells(1, 1, 1, 4);
  const title = ws.getCell(1, 1);
  title.value = 'Synthèse — Mes contributions';
  title.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
  title.alignment = { vertical: 'middle', indent: 1 };
  for (let c = 1; c <= 4; c++) ws.getCell(1, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
  ws.getRow(1).height = 28;

  const confirmed = contributions.filter(isConfirmed);
  const pending = contributions.filter(isPending);
  const rejected = contributions.filter(isRejected);
  const sum = (list) => list.reduce((s, c) => s + (c.amount || 0), 0);
  const totalConfirmed = sum(confirmed);
  const thisMonth = sum(confirmed.filter((c) => inSameMonth(c)));

  const sectionHeader = (label, rowIdx) => {
    ws.mergeCells(rowIdx, 1, rowIdx, 4);
    const cell = ws.getCell(rowIdx, 1);
    cell.value = label;
    cell.font = { bold: true, size: 12, color: { argb: GREEN } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_LIGHT } };
    cell.alignment = { vertical: 'middle', indent: 1 };
    ws.getRow(rowIdx).height = 22;
  };

  const kv = (label, value, rowIdx, money = false) => {
    const l = ws.getCell(rowIdx, 1);
    l.value = label;
    l.font = { color: { argb: SLATE } };
    l.border = { bottom: thin };
    const v = ws.getCell(rowIdx, 2);
    v.value = value;
    v.font = { bold: true };
    if (money) v.numFmt = MONEY_FMT;
    v.border = { bottom: thin };
  };

  let r = 3;
  sectionHeader('Montants', r++);
  kv('Total confirmé', totalConfirmed, r++, true);
  kv('Ce mois (confirmé)', thisMonth, r++, true);
  kv('En attente de validation', sum(pending), r++, true);
  kv('Rejeté', sum(rejected), r++, true);

  r++;
  sectionHeader('Volumes', r++);
  kv('Total transactions', contributions.length, r++);
  kv('Confirmées', confirmed.length, r++);
  kv('En attente', pending.length, r++);
  kv('Rejetées', rejected.length, r++);

  r++;
  sectionHeader('Répartition par type (confirmé)', r++);
  const catHeader = ws.getRow(r);
  ['Type', 'Nombre', 'Montant', '%'].forEach((h, i) => {
    const cell = catHeader.getCell(i + 1);
    cell.value = h;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN } };
    cell.alignment = { horizontal: i === 0 ? 'left' : 'center', indent: i === 0 ? 1 : 0 };
    cell.border = allBorders;
  });
  r++;

  const byType = new Map();
  confirmed.forEach((c) => {
    const key = typeLabel(c);
    byType.set(key, (byType.get(key) || { count: 0, total: 0 }));
    const e = byType.get(key);
    e.count += 1;
    e.total += c.amount || 0;
  });
  const rows = Array.from(byType.entries()).map(([label, e]) => ({ label, ...e })).sort((a, b) => b.total - a.total);
  rows.forEach((e) => {
    const cr = ws.getRow(r);
    cr.getCell(1).value = e.label;
    cr.getCell(2).value = e.count;
    cr.getCell(3).value = e.total;
    cr.getCell(4).value = totalConfirmed > 0 ? Math.round((e.total / totalConfirmed) * 100) : 0;
    cr.getCell(2).alignment = { horizontal: 'center' };
    cr.getCell(3).numFmt = MONEY_FMT;
    cr.getCell(4).numFmt = PCT_FMT;
    cr.getCell(4).alignment = { horizontal: 'center' };
    for (let c = 1; c <= 4; c++) cr.getCell(c).border = allBorders;
    cr.getCell(1).alignment = { indent: 1 };
    r++;
  });
}
